import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/Button/Button";
import { Header } from "../../components/Header/Header";
import Slider from "../../components/Slider/Slider";
import { VarianButtonEnum } from "../../enum/Button/variantButton.model";
import { useContext, useEffect, useLayoutEffect, useMemo, useReducer, useState } from "react";
import { VariantItem } from "../../components/Variant-Item/VariantItem";
import { DataContainerContext } from "../../context/DataContainerContext";
import dataContainer from "../../services/DataContainer";
import { AdobeCampaignService } from "../../services/adobe/AdobeCampaign";
import { AdobeEventType } from "../../enum/adobe/adobeEventType";
import { MainButtons, HiddenOptions, MainSettings, MainTopMenus } from "../../enum/enums";
import { IconModelEnum } from "../../enum/Button/iconButton.model";
import { buildConfiguratorFromURL } from "../../services/augmented-reality/augmentedReality";
import classNames from "classnames";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";
import "../../components/Slider/swiper.css";
import PanoramicViewROD from "../../components/PanoramicView360/panoramicViewROD";
import useWindowSize from "../../utils/useWindowSize";
import Turntable from "../../components/Turntable/Turntable";
import useShowExterior360 from "../../utils/useShowExterior360";
import { AdobeEventActions } from "../../enum/adobe/adobeEventActions";
import { StepNumbers } from "../../enum/adobe/stepNumbers";
import { StepNames } from "../../enum/adobe/stepNames";
import { SubStepNames, mapToAdobeComponentStructure} from "../../enum/adobe/subStepNames";
import { EventTriggers } from "../../enum/adobe/eventTriggers";
import { InfoShield } from "../../components/InfoShield/InfoShield";
import { Page } from "../../components/Page/Page";
import { Link } from "../../components/Menu/types";

export const Configurator = () => {
  const {
    data,
    currentLocale,
    activeComponent,
    activeComponentGroups,
    setActiveComponentIndex,
    allComponents,
    goNext,
    goBack,
    jumpToGroup,
    jumpToComponent,
    setModelName,
    searchParams,
    setProductDataLoaded
  } = useContext(DataContainerContext);
  const { modelName, componentFromUrl } = useParams();
  const [colorizerState, setColorizerState] = useState(true);
  const navigate = useNavigate();
  const component_groups = dataContainer.getComponentGroups()?.filter((item: any) => item.name !== HiddenOptions.HIDDEN_OPTIONS);

  const [viewPoints, setViewPoints] = useState<any>([]);
  const refreshTruckImage = useReducer((revision: number) => revision + 1, 0)[1];
  const {windowWidth} = useWindowSize();
  const isMobile = windowWidth < 640;
  const [interior360, setInterior360] = useState(!isMobile);

  const adobeService = new AdobeCampaignService();
  const prevActiveComponentGroupsName = adobeService.usePrevious(activeComponentGroups.name);
  const [outdoorsEnvironment, setOutdoorsEnvironment] = useState(true);
  const [exterior360, setExterior360] = useState(false);
  const [expandedColorizer, setExpandedColorizer] = useState(false);
  const showToggle360 = useShowExterior360();

  const handleNext = () => {
    dataContainer.actionTrigger = EventTriggers.NEXT;
    const hasNext = goNext();
    if (!hasNext) {
      dataContainer.getSelectedVariationsForPreviousComponentGroup(activeComponentGroups);
      const selections = localStorage.getItem("currentSelections");
      navigate(`/${currentLocale?.locale}/summary/${modelName}/${selections}`);
    }
  };

  const handleBack = () => {
    goBack();
  };

  const handleGroupClick = (groupIndex: number) => {
    dataContainer.actionTrigger = EventTriggers.MAIN_MENU;
    dataContainer.getSelectedVariationsForPreviousComponentGroup(activeComponentGroups);
    adobeService.setCategory(prevActiveComponentGroupsName);
    adobeService.sendAnalytics(AdobeEventType.PAGE_ENTERED, { category: prevActiveComponentGroupsName });
    jumpToGroup(groupIndex);
  };

  const handleComponentClick = (name: string) => {
    jumpToComponent(name);
  };

  useEffect(() => {
    const app = document.getElementById(process.env.REACT_APP_ID as string);
    if (app) app.classList.add("!overflow-y-hidden");
    return () => { app?.classList.remove("!overflow-y-hidden"); };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      dataContainer.sendDataToAdobeDataLayer(AdobeEventType.APPLICATION_INTERACTION, StepNumbers.STEP_2, StepNames.MODEL, SubStepNames.MODELS, AdobeEventActions.MODEL_SELECTED, {"Model": `Volvo ${modelName?.toUpperCase()}`}, "");
    }, 500);
  }, [modelName]);

  const matchingProduct = data?.find((item: any) =>
    item.id >= 183 && item.id < 188
      ? item.name.toLowerCase() === modelName
      : item.product_info?.description === modelName
  );

  useLayoutEffect(() => {
    const loadedProductId = dataContainer.getCurrentModelId();
    if (matchingProduct && loadedProductId && loadedProductId !== matchingProduct.id) {
      dataContainer.resetData();
      setProductDataLoaded(false);
      setActiveComponentIndex(0);
    }
  }, [matchingProduct, setActiveComponentIndex, setProductDataLoaded]);

  useEffect(() => {
    setModelName(modelName as string);
    if(dataContainer && dataContainer.mAttributes.length === 0){
      //automatic attributes selection based on market
      dataContainer.mAttributesDependingOnMarket.forEach((attr)=>{
        const productAttribute = dataContainer.getProductAttributes().find((attribute)=>attribute.name===attr);
        if(productAttribute){
          const defaultSelectionValue = dataContainer.sectionKey("Market.Settings", attr.toLowerCase());
          const attribute = dataContainer.findAttributeValueObjectByAttributeIdAndValue(productAttribute.id, defaultSelectionValue);

          if (attribute) {
            dataContainer.markAttributeAsSelected({...attribute, market_based: true});
          }
        }
      });
    }

    if (matchingProduct) {
      const onLoaded = () => {
        dataContainer.instance().mLoadedData;
        setActiveComponentIndex(0);
        setProductDataLoaded(true);
        setViewPoints(dataContainer.getProductViewPoints());
      };

      dataContainer.loadData(
        onLoaded,
        currentLocale as any,
        matchingProduct.id,
        matchingProduct.cached_url
      );

      if (dataContainer.getCurrentModelId() === matchingProduct.id) {
        onLoaded();
      }
    }
  }, [data, currentLocale, matchingProduct, modelName]);

  useEffect(() => {
    if (dataContainer.mLoadedData && searchParams) {
      buildConfiguratorFromURL(searchParams, component_groups);
    }
  }, [dataContainer.mLoadedData, searchParams]);

  useEffect(() => {
    if (dataContainer.mLoadedData) {
      if(!viewPoints?.length) setViewPoints(dataContainer.getProductViewPoints());
    }
  }, [dataContainer.mLoadedData, viewPoints]);

  useEffect(() => {
    if (componentFromUrl === "colorizer" && colorizerState && allComponents.length) {
      const colorsIndex = allComponents.findIndex((c: any) => c.groupName === "Colors");
      if (colorsIndex !== -1) {
        setActiveComponentIndex(colorsIndex);
        setColorizerState(false);
      }
    }
  }, [componentFromUrl, allComponents, colorizerState]);

  useEffect(() => {
    if (component_groups && activeComponentGroups.index >= 0) {
      const components = component_groups[activeComponentGroups.index]?.components;
      if (!components) return;
      
      const previousComponentIndex = activeComponent.index - 1;

      if (activeComponent.index > 0 && activeComponent.index < components.length) {
        const previousSubStep = mapToAdobeComponentStructure[components[previousComponentIndex]?.name as keyof typeof mapToAdobeComponentStructure];
        const toSubStep = mapToAdobeComponentStructure[activeComponent.name as keyof typeof mapToAdobeComponentStructure];
        const selectedVariations = dataContainer.getSelectedVariations();
        
        const previousSelectedVariation = selectedVariations.find((v) => v.component_id === components[previousComponentIndex].id);
        dataContainer.sendDataToAdobeDataLayer(
          AdobeEventType.APPLICATION_INTERACTION, 
          activeComponentGroups.index + 3, 
          activeComponentGroups.name.replaceAll("_", " "), 
          previousSubStep,
          AdobeEventActions.SUBSTEP_COMPLETED,
          {
            "Navigation Trigger": dataContainer.actionTrigger,
            [previousSubStep]: previousSelectedVariation?.key,
            "From Step": activeComponentGroups.name.replaceAll("_", " "),
            "From SubStep": previousSubStep,
            "To Step": activeComponentGroups.name.replaceAll("_", " "),
            "To SubStep": toSubStep
          }
        );
      }
    }
  }, [activeComponent, activeComponentGroups, component_groups]);

  const trimLevelsViewPoint = dataContainer.getAllProductViewPoints()?.find((n: any)=> n.name.includes("driverView"));
  const seatsViewPoint = dataContainer.getAllProductViewPoints()?.find((n: any)=> n.name.includes("bedView"));  
  const defaultViewPoint = dataContainer.getProductViewPoints()?.find((vp: any) => vp.name === "010");

  if (activeComponentGroups.name !== "Interior") {
    const arr = ["driverView", "bedView", "panoramic"];
    if (arr.includes(dataContainer.getCurrentPointOfViewName())) {
      dataContainer.setCurrentViewPoint(defaultViewPoint);
    }
  } else if (activeComponentGroups.name === "Interior") {
    if(activeComponent.name === "Int_trim_levels") {
      dataContainer.setCurrentViewPoint(trimLevelsViewPoint);
    }
    else if(activeComponent.name === "Seats") {
      dataContainer.setCurrentViewPoint(seatsViewPoint);
    }
  }

  const menuLinks = useMemo((): Link[] => {
    const components = dataContainer.getComponentByComponentGroupsId(activeComponentGroups.id) || [];

    return components
      .filter((i: any) => i.name !== "Dashboard")
      .sort((a: any, b: any) => a.order_no - b.order_no)
      .map((i: any) => ({
        id: i.id,
        link: i.link,
        name: i.name,
        label: dataContainer.sectionKey(
          MainTopMenus.MAIN_TOPMENU,
          `${activeComponentGroups.name.toLowerCase()}_${i.name.toLowerCase()}_title`
        ),
        activeClass: i.id === activeComponent?.componentId,
      }));
  }, [activeComponentGroups, activeComponent, dataContainer]);

  return (
    <Page fullHeight>
      <Header
        listSidebar={component_groups}
        onSidebarItemClickEvent={(group: any) => handleGroupClick(group.index)}
        links={menuLinks}
        title={
          activeComponentGroups.name
            ? dataContainer.sectionKey(
              MainSettings.MAIN_CATEGORIES,
              activeComponentGroups.name.toLowerCase()
            )
            : activeComponentGroups.name
        }
        onClickEvent={(component: any) => handleComponentClick(component.name)}
      />
      <div
        className={classNames(
          "flex relative flex-col z-0 justify-center flex-grow overflow-hidden transition-all transform duration-300 ease-in-out",
          { "items-center": activeComponentGroups.name !== "Interior" },
          { "h-0 flex-grow-0 ": expandedColorizer && isMobile }
        )}
      >
        <div className="h-full">
          {interior360 && activeComponentGroups.name === "Interior" ? (
            <div className="w-full h-full object-cover relative flex justify-center items-center">
              <div className="h-10 absolute top-6 left-4 sm:left-7 z-40 flex gap-2 !text-small">
                <Button
                  text={
                    !isMobile
                      ? outdoorsEnvironment
                        ? dataContainer.sectionKey(
                          MainButtons.MAIN_BUTTONS,
                          MainButtons.STUDIO_BUTTON
                        )
                        : dataContainer.sectionKey(
                          MainButtons.MAIN_BUTTONS,
                          MainButtons.OUTDOORS_BUTTON
                        )
                      : ""
                  }
                  variant={VarianButtonEnum.ICONBUTTON}
                  icon={
                    outdoorsEnvironment
                      ? IconModelEnum.Studio
                      : IconModelEnum.Outdoors
                  }
                  customClassNames="bg-light-grey-light transition-all transform duration-300 hover:bg-light-grey-hover w-10 sm:w-24"
                  onClickEvent={() => {
                    const nextState = !outdoorsEnvironment;
                    dataContainer.sendDataToAdobeDataLayer(
                      AdobeEventType.APPLICATION_INTERACTION,
                      activeComponentGroups.index + 3,
                      activeComponentGroups.name.replaceAll("_", " "),
                      mapToAdobeComponentStructure[
                        activeComponent.name as keyof typeof mapToAdobeComponentStructure
                      ],
                      AdobeEventActions.IMAGE_MODE_TOGGLED,
                      {
                        "Image Mode": nextState
                          ? IconModelEnum.Outdoors
                          : IconModelEnum.Studio,
                      }
                    );
                    setOutdoorsEnvironment(nextState);
                  }}
                />
                {activeComponentGroups.name === "Interior" && (
                  <div className="h-10 z-40 flex gap-2 !text-small">
                    <Button
                      variant={VarianButtonEnum.ICONBUTTON}
                      icon={
                        interior360
                          ? IconModelEnum.Interior360
                          : IconModelEnum.StaticInteriorImage
                      }
                      customClassNames="bg-light-grey-light transition-all transform duration-300 hover:bg-light-grey-hover w-10 h-10"
                      onClickEvent={() => setInterior360(!interior360)}
                    />
                  </div>
                )}
              </div>
              <PanoramicViewROD
                environment={outdoorsEnvironment ? "outdoor" : "studio"}
              />
            </div>
          ) : (
            viewPoints?.length && (
              <div className="h-full object-cover">
                <div className="max-w-20 sm:max-w-60 h-10 absolute top-6 left-4 sm:left-7 z-40 flex justify-between gap-2 !text-small">
                  {!exterior360 && (
                    <Button
                      text={
                        !isMobile
                          ? outdoorsEnvironment
                            ? dataContainer.sectionKey(
                              MainButtons.MAIN_BUTTONS,
                              MainButtons.STUDIO_BUTTON
                            )
                            : dataContainer.sectionKey(
                              MainButtons.MAIN_BUTTONS,
                              MainButtons.OUTDOORS_BUTTON
                            )
                          : ""
                      }
                      variant={VarianButtonEnum.ICONBUTTON}
                      icon={
                        outdoorsEnvironment
                          ? IconModelEnum.Studio
                          : IconModelEnum.Outdoors
                      }
                      customClassNames="bg-light-grey-light transition-all transform duration-300 hover:bg-light-grey-hover w-10 sm:w-24"
                      onClickEvent={() => {
                        const nextState = !outdoorsEnvironment;
                        dataContainer.sendDataToAdobeDataLayer(
                          AdobeEventType.APPLICATION_INTERACTION,
                          activeComponentGroups.index + 3,
                          activeComponentGroups.name.replaceAll("_", " "),
                          activeComponent.name,
                          AdobeEventActions.IMAGE_MODE_TOGGLED,
                          {
                            "Image Mode": nextState
                              ? IconModelEnum.Outdoors
                              : IconModelEnum.Studio,
                          }
                        );
                        setOutdoorsEnvironment(nextState);
                      }}
                    />
                  )}
                  {activeComponentGroups.name === "Interior" && (
                    <div className="h-10 z-40 flex gap-2 !text-small">
                      <Button
                        variant={VarianButtonEnum.ICONBUTTON}
                        icon={
                          interior360
                            ? IconModelEnum.Interior360
                            : IconModelEnum.StaticInteriorImage
                        }
                        customClassNames="bg-light-grey-light transition-all transform duration-300 hover:bg-light-grey-hover w-10 h-10"
                        onClickEvent={() => setInterior360(!interior360)}
                      />
                    </div>
                  )}
                  {showToggle360 && activeComponentGroups.name !== "Interior" && (
                    <Button
                      text={
                        !isMobile
                          ? !exterior360
                            ? "360 view"
                            : "close 360 view"
                          : ""
                      }
                      variant={VarianButtonEnum.ICONBUTTON}
                      icon={
                        !exterior360
                          ? IconModelEnum.Exterior360
                          : IconModelEnum.Close360
                      }
                      customClassNames={`bg-light-grey-light transition-all transform duration-300 hover:bg-light-grey-hover w-10 ${
                        !exterior360 ? "sm:w-28" : "sm:w-36"
                      }`}
                      onClickEvent={() => {
                        const nextState = !exterior360;

                        dataContainer.sendDataToAdobeDataLayer(
                          AdobeEventType.APPLICATION_INTERACTION,
                          activeComponentGroups.index + 3,
                          activeComponentGroups.name.replaceAll("_", " "),
                          mapToAdobeComponentStructure[
                            activeComponent.name as keyof typeof mapToAdobeComponentStructure
                          ],
                          nextState
                            ? AdobeEventActions.VIEW_360_OPENED
                            : AdobeEventActions.VIEW_360_CLOSED
                        );
                        setExterior360(nextState);
                      }}
                    />
                  )}
                </div>
                {exterior360 ? (
                  <Turntable />
                ) : (
                  <Slider
                    onSummary={false}
                    environment={outdoorsEnvironment ? "outdoor" : "studio"}
                  />
                )}
              </div>
            )
          )}
        </div>
        <div
          className={classNames(
            "transition-[max-height] max-h-full duration-300 ease-in-out",
            {
              "flex w-full justify-center items-center z-40":
                activeComponentGroups.name !== "Interior",
            },
            {
              "flex flex-row w-full justify-center bottom-0 absolute z-40 items-center pt-5":
                activeComponentGroups.name === "Interior",
            },
            { "max-h-0": exterior360 }
          )}
        >
          <div className="flex self-start gap-6 mb-2">
            <Button
              variant={VarianButtonEnum.BACKBUTTON}
              text={dataContainer.sectionKey(
                MainButtons.MAIN_BUTTONS,
                MainButtons.BACK_BUTTON
              )}
              textTransform={true}
              onClickEvent={handleBack}
            />
            <Button
              variant={VarianButtonEnum.NEXTBUTTON}
              text={dataContainer.sectionKey(
                MainButtons.MAIN_BUTTONS,
                MainButtons.NEXT_BUTTON
              )}
              textTransform={true}
              onClickEvent={handleNext}
            />
          </div>
        </div>
      </div>
      <div
        className={classNames(
          "transition-[max-height] duration-300 ease-in-out",
          {
            "max-h-0": exterior360,
            "max-h-full": !exterior360,
          }
        )}
      >
        <InfoShield />
        <VariantItem
          refreshTruckImage={refreshTruckImage}
          expandedColorizer={expandedColorizer}
          toggleColorizer={setExpandedColorizer}
          isMobile={isMobile}
        />
      </div>
    </Page>
  );
};
